import React, {
  PropsWithChildren,
  ReactNode,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import classes from './DatabaseSetup.module.scss';
import Typography from 'new/elements/Typography';
import TypographySupport from 'new/elements/TypographySupport';
import { Virtuoso } from 'react-virtuoso';
import { Stack } from 'new/elements/Stack';
import { ReactComponent as InfoIcon } from 'new/elements/flows/assets/info.svg';

import { ReactComponent as ArrowRight } from 'new/elements/flows/assets/arrow-small-right.svg';
import { ReactComponent as DatabaseIcon } from 'new/elements/flows/Databases/assets/database.svg';
import { ReactComponent as CodeRoundIcon } from 'new/components/assets/codeRounded.svg';
import { FormControlLabel, InputLabel } from '@mui/material';

import Tooltip from 'js/components/elements/mui/Tooltip';

import { Switch } from 'new/elements/Switch';
import { TextField } from 'new/elements/TextField';
import {
  getCurrentSelectedOption,
  getNewSelectionValue,
  SelectInput,
  SelectOption,
  redesignStyles,
} from 'js/components/elements/rselect/ReactSelectUtils';
import SortedSelect from 'js/components/elements/rselect/SortedSelect';
import cx from 'classnames';

import { Databases } from 'new/elements/flows/Databases';
import { ControlProps, SegmentedControl } from 'new/elements/SegmentControl/SegmentControl';
import { Tabs } from 'new/elements/Tabs';
import { TabProps } from 'new/elements/Tabs/Tabs';
import { useSelector } from 'react-redux';
import { Button } from 'new/elements/Button';
import { useImmer } from 'use-immer';
import { useDebouncedCallback } from 'new/hooks/useDebouncedCallback';
import useProbeTree from 'new/hooks/adapters/useProbeTree';
import { current } from 'immer';

import Menu from 'js/components/elements/menu/Menu';

import { CodeEditorMode } from 'new/elements/CodeEditorMode/CodeEditorMode';

import JSONUtils from 'js/lib/JSONUtils';
import { useDistance } from 'new/hooks/useDistance';
import { MappingType } from 'new/elements/flows/Databases/useDatabases';

import { isEmpty } from 'lodash';
import {
  mapToSinkConfig,
  parseSinkConfig,
} from 'new/components/flows/types/elt/CreateELTFlow/DestinationSetupPage/sinkConfigMapper';
import { isUiModeCode } from 'new/utils/uiMode';
import { UiMode } from 'new/constants/uiMode';

interface DatabaseSetupProps {
  source: any;
  sink: any;
  onConfigUpdated: (config: any) => void;
  credentialId: number;
  resourceType: 'SOURCE' | 'SINK';
  resourceConfig?: any;
  onRequestProbeTree: (node: any) => void;
  onClickEditConfig?: () => void;
  connectorType: string;
  connectionCategory: string;
  parentScrollRef?: RefObject<HTMLDivElement>;
  onDiscardAdvancedForm: () => void;
  onSubmitAdvancedForm: (resourceConfig: any) => void;
  initialMapping?: MappingType;
}

const SectionInfo = ({ children }) => {
  return <div className={classes.sectionInfo}>{children}</div>;
};

const Slot = ({ children, alignment }: PropsWithChildren<{ alignment?: string }>) => {
  return (
    <div
      className={cx(classes.sectionInfoSlot, {
        [classes['sectionInfoSlot_' + alignment]]: !!alignment,
      })}
    >
      {children}
    </div>
  );
};

const Resource = ({
  name,
  state,
  actions,
  connectorType,
  resourceType,
}: {
  name?: string;
  state?: 'idle' | 'done' | 'active';
  actions?: { label: string; icon?: ReactNode; onClick: () => void }[];
  connectorType?: string;
  resourceType: 'SOURCE' | 'SINK';
}) => {
  const metas = useSelector((state: any) => state.paging.metaInfoById);
  const meta = metas[connectorType];
  return (
    <div
      className={cx(classes.resourceContainer, {
        [classes.resourceContainer_done]: state === 'done',
        [classes.resourceContainer_active]: state === 'active',
      })}
    >
      <div className={classes.resourceImgContainer}>
        {meta?.iconSm ? <img src={meta.iconSm} /> : <DatabaseIcon />}
      </div>
      <Stack>
        <Typography variant="overline">
          {resourceType === 'SOURCE' ? 'SOURCE' : 'DESTINATION'}
        </Typography>
        <Typography variant="body2">{meta?.name || 'Configured in next steps'}</Typography>
      </Stack>
      {actions ? (
        <Stack flex={1} alignItems="flex-end">
          <Menu items={actions} horizontalDots />
        </Stack>
      ) : null}
    </div>
  );
};

const SectionInfoTitle = ({
  children,
  tooltipTitle,
  htmlFor,
}: PropsWithChildren<{ tooltipTitle?: string; htmlFor?: string }>) => {
  return (
    <Stack className={classes.sectionInfoTitle}>
      <InputLabel htmlFor={htmlFor}>
        <Typography variant="body2">{children}</Typography>
      </InputLabel>
      {tooltipTitle ? (
        <Tooltip
          interactive="true"
          placement="right"
          rootStyle={{}}
          arrow={false}
          tooltipTheme="fieldInfo"
          title={tooltipTitle}
          children={
            <div style={{ marginTop: 'var(--spacing-sm)' }}>
              <InfoIcon />
            </div>
          }
        />
      ) : null}
    </Stack>
  );
};

const schemaDetectionControls = [
  {
    label: 'Automatic',
    value: 'auto',
  },
  {
    label: 'Select by name',
    value: 'name',
  },
  {
    label: 'Select by pattern',
    value: 'pattern',
  },
] as ControlProps[];

const schemaDetectionSinkControls = [
  {
    label: 'Automatic',
    value: 'auto',
  },
  {
    label: 'Customize (Coming Soon)',
    value: 'name',
    disabled: true,
  },
] as ControlProps[];

const sinkTabs = [
  { label: 'View Source Feeds', value: 'feeds' },
  { label: 'Browse Destination DB', value: 'db' },
] as TabProps[];

const tableInsertModes = [
  {
    label: 'Insert',
    value: 'INSERT',
    description: 'Select this mode for inserting all new records into resepctive tables',
  },
  {
    label: 'Upsert',
    value: 'UPSERT',
    description:
      'Select this mode for upserting new records into resepctive tables. Unique identifiers for each upstream feed are used to determine if a record should be inserted or updated.',
  },
];

const DestinationInfoSection = ({ setConfig, config, credentialId }) => {
  const { databases, probeDataFlat } = useProbeTree(credentialId);

  //re-render text field when the databse has been loaded and the text field is still empty.
  useEffect(() => {
    if (databases.length && !config.namespace) {
      setConfig((config) => {
        config.namespace = databases[0].name;
      });
    }
  }, [databases]);

  const onChangeField = (e) => {
    //we want same name as the table name in destination after probe tree. if probe tree succeeded then fine. else let user set the name
    const { name, value, checked, type } = e.target as HTMLInputElement;
    if (type === 'checkbox') {
      setConfig((config) => {
        config[name] = checked;
      });
      return;
    }
    setConfig((config) => {
      config[name] = value;
    });
  };

  const currentTableInsertMode = getCurrentSelectedOption(
    tableInsertModes,
    config['tableInsertMode'],
  );

  return (
    <SectionInfo>
      <Slot>
        <SectionInfoTitle
          htmlFor="namespace"
          tooltipTitle="Name of the database where the tables will be created"
        >
          Database Name *
        </SectionInfoTitle>
        <TextField
          id="namespace"
          name="namespace"
          value={
            typeof config.namespace === 'string' ? config.namespace : databases?.[0]?.name || ''
          }
          data-testid="sink-namespace"
          onChange={onChangeField}
          placeholder=""
          fullWidth
        />
      </Slot>
      <Slot />
      <Slot>
        <SectionInfoTitle
          htmlFor="tablePrefix"
          tooltipTitle="(Optional) Prefix to be applied to all tables names."
        >
          Table Name Prefix
        </SectionInfoTitle>
        <TextField
          id="tablePrefix"
          name="tablePrefix"
          data-testid="sink-table-prefix"
          defaultValue={config.tablePrefix}
          onChange={onChangeField}
          placeholder=""
          fullWidth
        />
      </Slot>
      <Slot />
      <Slot>
        <SectionInfoTitle
          htmlFor="tableSuffix"
          tooltipTitle="(Optional) Suffix to be applied to all table names. "
        >
          Table Name Suffix
        </SectionInfoTitle>
        <TextField
          id="tableSuffix"
          name="tableSuffix"
          data-testid="sink-table-suffix"
          defaultValue={config.tableSuffix}
          onChange={onChangeField}
          fullWidth
        />
      </Slot>
      <Slot />
      <Stack direction="row" alignItems="center" flexBasis="100%">
        <Stack flexBasis="25%">
          <SectionInfoTitle
            htmlFor="tableInsertMode"
            tooltipTitle="Select whether records should be inserted or upserted into the database"
          >
            Table Update Mode
          </SectionInfoTitle>
        </Stack>
        <Stack direction="row" alignItems="center" flexBasis="37%">
          <SortedSelect
            value={currentTableInsertMode}
            options={tableInsertModes}
            components={{ Input: SelectInput, Option: SelectOption }}
            styles={redesignStyles}
            className={cx(classes.tableInsertMode)}
            isClearable={false}
            backspaceRemovesValue={false}
            isSearchable
            onChange={(option) => {
              let newValue = getNewSelectionValue(option, true);
              onChangeField({
                target: { name: 'tableInsertMode', value: newValue },
              });
            }}
          />
        </Stack>
        <Stack direction="row" alignItems="center" flexBasis="37%" justifyContent="flex-end">
          <FormControlLabel
            htmlFor="trackerEnabled"
            control={
              <Switch
                id="trackerEnabled"
                name="trackerEnabled"
                checked={!!config.trackerEnabled}
                onChange={onChangeField}
                color="primary"
              />
            }
            label="Track Lineage"
            labelPlacement="end"
          />

          <Stack width="290px">
            <TextField
              fullWidth
              defaultValue={config.trackerName}
              name="trackerName"
              data-testid="trackername-input"
              onChange={onChangeField}
              placeholder="__nexla_tracker_id"
              disabled={!config.trackerEnabled}
            />
          </Stack>
        </Stack>
      </Stack>
    </SectionInfo>
  );
};

const Configuration = ({ resourceType, setConfig, config, tab, source }) => {
  if (tab !== 'feeds') return null;
  const { schemaMode } = config;

  const [search, setSearch] = useState('');

  const onControlChange = (event, value) => {
    setConfig((config) => {
      config.schemaMode = value;
    });
  };

  const onSelectionChange = (selection) => {
    setConfig((config) => {
      config.selection = selection;
    });
  };

  const onMappingChange = (mapping) => {
    setConfig((config) => {
      config.mapping = mapping;
    });
  };

  const onSearchInputChanged = (search) => {
    setSearch(search);
  };

  return (
    <>
      <div className={cx(classes.sectionInfo, classes.sectionInfoDatabase)}>
        <Stack flexBasis="25%" flexGrow={0}>
          <SectionInfoTitle>
            {resourceType === 'SINK'
              ? `Feed \u2192 Database Mapping Mode`
              : 'Schema selection mode'}
          </SectionInfoTitle>
        </Stack>
        <Stack flexBasis="50%" flexGrow={0}>
          <SegmentedControl
            controls={
              resourceType === 'SINK' ? schemaDetectionSinkControls : schemaDetectionControls
            }
            defaultValue="auto"
            value={schemaMode}
            onControlChange={onControlChange}
          />
        </Stack>
        <Stack flexBasis="15%" flexGrow={0}>
          <TextField
            value={search}
            onChange={(e) => onSearchInputChanged(e.target.value)}
            placeholder="Filter Feeds"
            id="name"
            name="name"
            inputProps={{
              'data-testid': 'resource-name-input',
            }}
          />
        </Stack>
      </div>
      {schemaMode === 'name' && resourceType === 'SINK' ? (
        <div className={classes.emptyState}>
          <Typography variant="h2">
            Streams are selected by pattern in Source configuration
          </Typography>
          <Button variant="outlined" color="inherit">
            Controls for customizing mapping of data from feeds to tables
          </Button>
        </div>
      ) : (
        <FeedsBrowser source={source} config={config} search={search} />
      )}
    </>
  );
};

const FeedsBrowser = ({ source, config, search = '' }) => {
  const feeds = useMemo(() => {
    const sourceFeeds = source?.template_config?.feeds_config?.feeds || {};
    let list = [];

    list = Object.keys(sourceFeeds).map((feedName) => {
      const feed = sourceFeeds[feedName];
      return {
        label: feed?.spec?.params['ui.feed_display_name'] || feedName,
        value: feedName,
        feedProps: feed,
      };
    });

    if (search) {
      list = list.filter((feed) => feed.label.toLowerCase().includes(search.toLowerCase()));
    }

    return { list, feedSpecs: sourceFeeds };
  }, [source, search]);

  const itemRenderer = useCallback(
    (index, { label, value }) => {
      const id = `feed-item-${value}`;
      return (
        <div key={value} className={classes.feedsListItem}>
          <TypographySupport variant="body1" className={classes.feedTitle}>
            {label}
          </TypographySupport>
          <TypographySupport variant="body1"> {`\u2192`}</TypographySupport>
          <TypographySupport variant="body1" className={classes.tableName}>
            {config['tablePrefix'] || ''}
            {value}
            {config['tableSuffix'] || ''}
          </TypographySupport>
        </div>
      );
    },
    [feeds, config],
  );

  return (
    <div className={classes.feedsList}>
      <div className={classes.feedsListHeader}>
        <div className={classes.feedsListItem}>
          <TypographySupport variant="h6" className={classes.feedTitle}>
            Feed Name
          </TypographySupport>
          <TypographySupport variant="body1"> {`\u2192`}</TypographySupport>
          <TypographySupport variant="h6" className={classes.tableName}>
            Table Name
          </TypographySupport>
        </div>
      </div>
      <div className={classes.feedsListBody}>
        {!isEmpty(feeds.list) && (
          <Virtuoso
            className={classes.feedsListVirtuoso}
            data={feeds.list}
            itemContent={itemRenderer}
          />
        )}
      </div>
    </div>
  );
};

const DBBrowser = ({ tab, onRequestProbeTree, credentialId }) => {
  const { databases } = useProbeTree(credentialId);
  if (tab !== 'db') return null;
  return (
    <>
      <div className={classes.databaseTitle}>
        <SectionInfoTitle>
          Review the current database structure and tables to inform your decisions regarding the
          destination for the selected source feeds.
        </SectionInfoTitle>
      </div>
      <Databases databases={databases} credentialId={credentialId} />
    </>
  );
};

const sinkInitialState = {
  name: '',
  schemaMode: 'auto',
  tablePrefix: '',
  tablesSuffix: '',
  tableInsertMode: 'UPSERT',
};

export const DatabaseSetup = (props: DatabaseSetupProps) => {
  const { onDiscardAdvancedForm, connectorType } = props;
  const [showAdvancedMode, setShowAdvancedMode] = useState(isUiModeCode(props?.resourceConfig));

  const topRef = useRef();
  const editorHeight = useDistance(topRef);

  const vendors = useSelector((state: any) => state.entities.vendors);
  // Probe data is also fetched inside DBBrowser / DestinationInfoSection via
  // useProbeTree(credentialId); useProbeTree is memoized so this second call
  // returns the same data and the cost is negligible. We need access at this
  // scope to enrich the JSON drawer's display with the probe-derived database
  // default (NEX-21470, view→code transition only — display-only).
  const { databases: probedDatabases } = useProbeTree(props.credentialId);
  const [resourceConfig, setImmerConfig] = useImmer(props.resourceConfig || sinkInitialState);
  const [tab, setTab] = useState('feeds');

  useEffect(() => {
    setImmerConfig((config) => {
      config = props.resourceConfig;
    });
  }, [props.resourceConfig]);

  const onTabChange = (e, value) => {
    setTab(value);
  };

  const setConfig = (updater) => {
    setImmerConfig((config) => {
      updater(config);
      props.onConfigUpdated(current(config));
    });
  };

  const onChangeName = useDebouncedCallback(
    (value) => {
      setConfig((resourceConfig) => {
        resourceConfig.name = value;
      });
    },
    [],
    500,
  );

  const sourceConnector = (() => {
    const vendorId = props.source?.vendor_id;
    if (vendorId && vendors[vendorId]) {
      return vendors[vendorId].name;
    }
    return props.source?.connector_type || props.connectorType;
  })();

  const sinkConnector = (() => {
    if (props.resourceType === 'SINK') return props.connectorType;
  })();

  const onClickEditConfig = () => {
    setShowAdvancedMode(true);
  };

  const onCloseAdvancedMode = () => {
    if (isUiModeCode(resourceConfig)) {
      if (typeof onDiscardAdvancedForm === 'function') {
        onDiscardAdvancedForm();
      }
      return;
    }
    setShowAdvancedMode(false);
  };
  const onSaveAdvancedMode = (configString: string) => {
    const parse = parseSinkConfig;
    if (JSONUtils.isJSON(configString)) {
      setImmerConfig(() => {
        const newConfig = {
          ...parse(JSON.parse(configString)),
          ['ui.mode']: UiMode.CODE,
        };
        if (typeof props.onSubmitAdvancedForm === 'function') {
          props.onSubmitAdvancedForm(newConfig);
        }
        return newConfig;
      });
    }
  };

  const onSwitchUiMode = () => {
    setConfig((config: any) => {
      config['ui.mode'] = UiMode.VIEW;
    });
    setShowAdvancedMode(false);
  };

  return (
    <div className={classes.setupContainer}>
      <div ref={topRef} className={classes.resourcesCardContainer}>
        <Resource resourceType="SOURCE" connectorType={sourceConnector} state={'done'} />

        <ArrowRight />
        <Resource
          connectorType={sinkConnector}
          resourceType="SINK"
          actions={
            props.resourceType === 'SINK'
              ? [
                  {
                    label: 'View JSON Config',
                    icon: <CodeRoundIcon />,
                    onClick: onClickEditConfig,
                  },
                ]
              : undefined
          }
          state={props.resourceType === 'SINK' ? 'active' : 'idle'}
        />
      </div>
      <Stack
        flex={1}
        position="relative"
        id="databaseSetupContainer"
        className={classes.databaseSetupContainer}
      >
        <SectionInfo>
          <Slot>
            <SectionInfoTitle htmlFor="name">Destination Name</SectionInfoTitle>
            <TextField
              id="name"
              name="name"
              inputProps={{
                'data-testid': 'resource-name-input',
              }}
              defaultValue={resourceConfig.name}
              onChange={(e) => onChangeName(e.target.value)}
              placeholder="Name"
              fullWidth
            />
          </Slot>
        </SectionInfo>
        <DestinationInfoSection
          setConfig={setConfig}
          config={resourceConfig}
          credentialId={props.credentialId}
        />

        <div className={classes.databaseSection}>
          <Tabs tabs={sinkTabs} value={tab} onChange={onTabChange} />
          <Configuration
            source={props.source}
            setConfig={setConfig}
            config={resourceConfig}
            resourceType={props.resourceType}
            tab={tab}
          />

          <DBBrowser
            onRequestProbeTree={props.onRequestProbeTree}
            credentialId={props.credentialId}
            tab={tab}
          />
        </div>
        <CodeEditorMode
          cancelable={!isUiModeCode(resourceConfig)}
          editorHeight={editorHeight - 100}
          open={showAdvancedMode}
          config={(() => {
            const base = mapToSinkConfig(resourceConfig);
            // NEX-21470: when the user is transitioning from VIEW into CODE
            // mode (no ui.mode flag yet), surface the save-time defaults in
            // the drawer so they can be seen and edited. Without this they
            // were silently injected at save time by
            // `useResourceCreation.createNewSink` and `createDefaultSinkConfig`.
            // Display-only enrichment — form state and view-mode behavior
            // are not affected. Once the resource is in code mode the existing
            // config is shown verbatim.
            if (isUiModeCode(resourceConfig)) return base;
            const inner = { ...(base as any).sink_config };
            const probedDb = probedDatabases?.[0]?.name;
            if (probedDb && inner.database === undefined) inner.database = probedDb;
            if (inner['insert.mode'] === undefined) inner['insert.mode'] = 'UPSERT';
            if (inner['upsert.nulls'] === undefined) inner['upsert.nulls'] = true;
            return { ...(base as any), sink_config: inner };
          })()}
          onClose={onCloseAdvancedMode}
          onSave={onSaveAdvancedMode}
          onSwitchUiMode={onSwitchUiMode}
          connectorType={connectorType}
        />
      </Stack>
    </div>
  );
};
